import * as THREE from 'three';
import type { Player } from '@/gameplay/Player';

export const OCCLUSION = {
  targetOpacity: 0.38,
  fadeInSeconds: 0.12,
  fadeOutSeconds: 0.18,
  rayPadding: 0.12,
} as const;

interface OccluderState {
  readonly mesh: THREE.Mesh;
  readonly sourceMaterials: readonly THREE.Material[];
  readonly originals: readonly MaterialSnapshot[];
  readonly materialWasArray: boolean;
  alpha: number;
  materialBucket: number;
}

interface MaterialSnapshot {
  readonly opacity: number;
  readonly transparent: boolean;
  readonly depthWrite: boolean;
}

export class OcclusionSystem {
  readonly #camera: THREE.Camera;
  readonly #player: Player;
  readonly #raycaster = new THREE.Raycaster();
  readonly #origin = new THREE.Vector3();
  readonly #target = new THREE.Vector3();
  readonly #direction = new THREE.Vector3();
  readonly #states = new Map<THREE.Mesh, OccluderState>();
  readonly #active = new Set<THREE.Mesh>();
  readonly #materials = new OcclusionMaterialCache();
  #occluded = false;

  constructor(scene: THREE.Scene, camera: THREE.Camera, player: Player) {
    void scene;
    this.#camera = camera;
    this.#player = player;
  }

  get occluded(): boolean {
    return this.#occluded;
  }

  get activeCount(): number {
    return this.#active.size;
  }

  get registeredCount(): number {
    return this.#states.size;
  }

  register(root: THREE.Object3D): void {
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      if (object.userData.naOccluder !== true) return;
      if (this.#states.has(object)) return;

      const materialWasArray = Array.isArray(object.material);
      const sourceMaterials = materialWasArray ? [...object.material] : [object.material];
      const originals = sourceMaterials.map(snapshotMaterial);
      this.#states.set(object, { mesh: object, sourceMaterials, originals, materialWasArray, alpha: 0, materialBucket: -1 });
    });
  }

  unregister(root: THREE.Object3D): void {
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const state = this.#states.get(object);
      if (!state) return;

      restoreSourceMaterials(state);
      this.#states.delete(object);
      this.#active.delete(object);
    });
    this.#occluded = this.#active.size > 0;
  }

  update(dt: number): void {
    this.#active.clear();
    this.#findActiveOccluders();

    const fadeInStep = dt / OCCLUSION.fadeInSeconds;
    const fadeOutStep = dt / OCCLUSION.fadeOutSeconds;

    for (const state of this.#states.values()) {
      const target = this.#active.has(state.mesh) ? 1 : 0;
      const step = target > state.alpha ? fadeInStep : fadeOutStep;
      state.alpha = moveToward(state.alpha, target, step);
      applyFade(state, this.#materials);
    }

    this.#occluded = this.#active.size > 0;
  }

  dispose(): void {
    for (const state of this.#states.values()) {
      restoreSourceMaterials(state);
    }
    this.#materials.dispose();
    this.#states.clear();
    this.#active.clear();
  }

  #findActiveOccluders(): void {
    this.#player.chestPoint(this.#target);
    this.#camera.getWorldPosition(this.#origin);

    const targetDistance = this.#origin.distanceTo(this.#target) - OCCLUSION.rayPadding;
    if (targetDistance <= 0) return;

    this.#direction.subVectors(this.#target, this.#origin).normalize();
    this.#raycaster.set(this.#origin, this.#direction);
    this.#raycaster.near = 0;
    this.#raycaster.far = targetDistance;

    const hits = this.#raycaster.intersectObjects([...this.#states.keys()], false);
    for (const hit of hits) {
      this.#active.add(hit.object as THREE.Mesh);
    }
  }

}

function snapshotMaterial(material: THREE.Material): MaterialSnapshot {
  return {
    opacity: material.opacity,
    transparent: material.transparent,
    depthWrite: material.depthWrite,
  };
}

function applyFade(state: OccluderState, cache: OcclusionMaterialCache): void {
  const bucket = fadeBucket(state.alpha);
  if (bucket === state.materialBucket) return;

  state.materialBucket = bucket;
  if (bucket === 0) {
    restoreSourceMaterials(state);
    return;
  }

  const materials = state.sourceMaterials.map((material, index) => (
    cache.materialFor(material, state.originals[index]!, bucket)
  ));
  state.mesh.material = state.materialWasArray ? materials : materials[0]!;
}

function moveToward(value: number, target: number, step: number): number {
  if (value < target) return Math.min(target, value + step);
  if (value > target) return Math.max(target, value - step);
  return value;
}

const FADE_BUCKETS = 24;

function fadeBucket(alpha: number): number {
  if (alpha <= 0.001) return 0;
  return Math.max(1, Math.min(FADE_BUCKETS, Math.round(alpha * FADE_BUCKETS)));
}

function restoreSourceMaterials(state: OccluderState): void {
  state.mesh.material = state.materialWasArray ? [...state.sourceMaterials] : state.sourceMaterials[0]!;
}

class OcclusionMaterialCache {
  readonly #materials = new Map<string, THREE.Material>();

  materialFor(source: THREE.Material, original: MaterialSnapshot, bucket: number): THREE.Material {
    const key = `${source.uuid}:${bucket}`;
    const cached = this.#materials.get(key);
    if (cached) return cached;

    const alpha = bucket / FADE_BUCKETS;
    const material = source.clone();
    material.name = `${source.name || 'NA_Mat'}_Occlusion_${bucket}`;
    material.opacity = THREE.MathUtils.lerp(original.opacity, OCCLUSION.targetOpacity, alpha);
    material.transparent = true;
    material.depthWrite = false;
    material.needsUpdate = true;
    this.#materials.set(key, material);
    return material;
  }

  dispose(): void {
    for (const material of this.#materials.values()) material.dispose();
    this.#materials.clear();
  }
}
